import { Button as PrimeButton, ButtonProps as PrimeButtonProps } from "primereact/button";
import { twMerge } from "tailwind-merge";

export const BUTTON_SIZES = Object.freeze({
    SMALL: "SMALL",
    BASE: "BASE",
    LARGE: "LARGE",
} as const);

export type ButtonSize = (typeof BUTTON_SIZES)[keyof typeof BUTTON_SIZES];

export const BUTTON_SEVERITIES = Object.freeze({
    NEUTRAL: "NEUTRAL",
    PRIMARY: "PRIMARY",
    SECONDARY: "SECONDARY",
    TERTIARY: "TERTIARY",
    ACCENT: "ACCENT",
    INFO: "INFO",
    SUCCESS: "SUCCESS",
    WARNING: "WARNING",
    DANGER: "DANGER",
} as const);

export type ButtonSeverity =
    (typeof BUTTON_SEVERITIES)[keyof typeof BUTTON_SEVERITIES];

export interface ButtonProps
    extends Omit<PrimeButtonProps, "size" | "severity"> {
    size?: ButtonSize;
    severity?: ButtonSeverity;
}

const PRIME_SIZE_MAP = Object.freeze({
    [BUTTON_SIZES.SMALL]: "small",
    [BUTTON_SIZES.BASE]: undefined,
    [BUTTON_SIZES.LARGE]: "large",
} as const);

const PRIME_SEVERITIES: Partial<
    Record<ButtonSeverity, PrimeButtonProps["severity"]>
> = Object.freeze({
    [BUTTON_SEVERITIES.SECONDARY]: "secondary",
    [BUTTON_SEVERITIES.INFO]: "info",
    [BUTTON_SEVERITIES.SUCCESS]: "success",
    [BUTTON_SEVERITIES.WARNING]: "warning",
    [BUTTON_SEVERITIES.DANGER]: "danger",
});

const CUSTOM_SEVERITY_CLASSES: Partial<Record<ButtonSeverity, string>> =
    Object.freeze({
        [BUTTON_SEVERITIES.NEUTRAL]: "p-button-neutral",
        [BUTTON_SEVERITIES.ACCENT]: "p-button-accent",
        [BUTTON_SEVERITIES.TERTIARY]: "p-button-tertiary",
    });

const Button = (props: ButtonProps) => {
    const {
        size = BUTTON_SIZES.BASE,
        severity = BUTTON_SEVERITIES.PRIMARY,
        className,
        ...rest
    } = props;

    const primeSize = PRIME_SIZE_MAP[size];
    const primeSeverity = PRIME_SEVERITIES[severity];

    return (
        <PrimeButton
            {...rest}
            size={primeSize}
            severity={primeSeverity}
            className={twMerge(CUSTOM_SEVERITY_CLASSES[severity], className)}
        />
    );
};

export default Button;
